import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrophy } from '@fortawesome/free-solid-svg-icons';
import './PlayerFlow.css';
import { getGameDetails } from '../../apiservices/LocalStorageService';
import Confetti from '../animations/Confetti';

const podiumColors = ['#8B8B8B', '#D4A843', '#8B7355'];

interface Props {
  leaderboard?: LeaderboardEntry[];
  yourPlayerId?: String | undefined;
}

const PlayerGameOver: React.FC<Props> = ({ leaderboard, yourPlayerId }) => {
  const navigate = useNavigate();

  const gamedetail = getGameDetails();
  const { gameTitle, totalQuestion } = gamedetail || {};
  const you = leaderboard.find((p) => p.playerId === yourPlayerId);
  const YourRank =
    leaderboard.findIndex((p) => p.playerId === yourPlayerId) + 1;
  const top3 = leaderboard
    ?.sort((a, b) => {
      if (a.totalPoints !== b.totalPoints) {
        return b.totalPoints - a.totalPoints;
      }
      return a.totalPoints - b.totalPoints;
    })
    .slice(0, 3);
  const podiumOrder = top3.length >= 3 ? [top3[1], top3[0], top3[2]] : top3;
  const totalPlayers = leaderboard?.length;
  const totalTimeSpent = Math.max(
    ...(leaderboard?.map((player) => player.totalTime) || [0])
  );
  const avgAccuracy = Math.round(
    ((leaderboard?.reduce((sum, player) => sum + player.correctCount, 0) || 0) /
      ((totalPlayers || 1) * (totalQuestion || 1))) *
      100
  );

  return (
    <div className="pgo">
      <Confetti active />
      <div className="pgo__left">
        <button
          className="pgo__back"
          onClick={() => navigate('/join', { state: { clearOldGame: true } })}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
        </button>
        <div className="pgo__hero">
          <h1>
            <FontAwesomeIcon icon={faTrophy} /> Game Over!
          </h1>
          <p>
            {gameTitle} • {leaderboard?.length} players • {totalQuestion}{' '}
            questions
          </p>
        </div>

        {/* Podium */}
        <div className="pgo__podium">
          {podiumOrder.map((player, i) => {
            const place = podiumOrder.length >= 3 ? [2, 1, 3][i] : i + 1;
            return (
              <div key={player.playerName} className="pgo__podium-col">
                {place === 1 && <span className="pgo__crown">👑</span>}
                {place === 2 && <span className="pgo__medal">🥈</span>}
                {place === 3 && <span className="pgo__medal">🥉</span>}
                <span
                  className="pgo__podium-avatar"
                  style={{
                    background: player.iconColor,
                    borderColor:
                      place === 1 ? 'var(--color-primary)' : player.iconColor,
                  }}
                >
                  {player.playerIcon}
                </span>
                <span className="pgo__podium-name">{player.playerName}</span>
                <div
                  className={`pgo__podium-block pgo__podium-block--${place}`}
                  style={{ background: podiumColors[i] }}
                >
                  <span className="pgo__podium-place">{place}</span>
                  <span className="pgo__podium-score">
                    {player.totalPoints}
                  </span>
                  {place === 1 && (
                    <span className="pgo__podium-label">CHAMPION</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Your Performance */}
        <div className="pgo__perf">
          <h3>🏆 Your Performance</h3>
          <div className="pgo__perf-grid">
            <div className="pgo__perf-card">
              <span className="pgo__perf-val">#{YourRank}</span>
              <span className="pgo__perf-lbl">Rank</span>
              <span className="pgo__perf-sub">out of {leaderboard.length}</span>
            </div>
            <div className="pgo__perf-card">
              <span className="pgo__perf-val">{you.totalPoints}</span>
              <span className="pgo__perf-lbl">Score</span>
              <span className="pgo__perf-sub">points</span>
            </div>
            <div className="pgo__perf-card">
              <span className="pgo__perf-val">
                {you.correctCount
                  ? Math.round(you.correctCount / totalQuestion || 10) * 100
                  : 0}
                %
              </span>
              <span className="pgo__perf-lbl">Accuracy</span>
              <span className="pgo__perf-sub">correct</span>
            </div>
            <div className="pgo__perf-card">
              <span className="pgo__perf-val">
                {you.totalTime >= 60
                  ? `${Math.floor(you.totalTime / 60)}m ${you.totalTime % 60}s`
                  : `${you.totalTime}s`}
              </span>
              <span className="pgo__perf-lbl">Time taken</span>
              <span className="pgo__perf-sub">this game</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="pgo__right">
        <h2>Final Results</h2>
        <p className="pgo__sub">{gameTitle}</p>
        <div className="pgo__stats">
          <div className="pgo__stat">
            <span className="pgo__stat-val">{totalPlayers}</span>
            <span className="pgo__stat-lbl">Players</span>
          </div>
          <div className="pgo__stat">
            <span className="pgo__stat-val">{avgAccuracy} %</span>
            <span className="pgo__stat-lbl">Avg Accuracy</span>
          </div>
          <div className="pgo__stat">
            <span className="pgo__stat-val">
              {totalTimeSpent >= 60
                ? `${Math.floor(totalTimeSpent / 60)}m ${totalTimeSpent % 60}s`
                : `${totalTimeSpent}s`}
            </span>
            <span className="pgo__stat-lbl">Duration</span>
          </div>
        </div>
        <h4 className="pgo__rankings-title">Final Rankings</h4>
        <div className="pgo__rankings">
          {leaderboard.map((player, rank) => (
            <div
              key={player.playerName}
              className={`pgo__rank-row ${rank <= 3 ? 'pgo__rank-row--top' : ''}`}
            >
              <span className="pgo__rank-num">#{rank + 1}</span>
              <span
                className="pgo__rank-avatar"
                style={{ background: player.iconColor }}
              >
                {player.playerIcon}
              </span>
              <div className="pgo__rank-info">
                <span className="pgo__rank-name">{player.playerName}</span>
                <span className="pgo__rank-detail">
                  {player.correctCount || 0}/{totalQuestion || 10} correct • avg{' '}
                  {player.avgTime || '—'}
                </span>
              </div>
              <span className="pgo__rank-score">{player.totalPoints}</span>
            </div>
          ))}
        </div>
        {/* <button className="pgo__btn pgo__btn--secondary">
          <FontAwesomeIcon icon={faDownload} /> Download Full CSV Report
        </button>
        <button className="pgo__btn pgo__btn--outline">
          <FontAwesomeIcon icon={faShareNodes} /> Share Results
        </button> */}
      </div>
    </div>
  );
};

export default PlayerGameOver;
